import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import QRCode from 'qrcode';
import { ChevronLeft, Printer } from 'lucide-react';
import { BrancheFlorale } from '../components/Florals';

const OPTIONS = { margin: 1, color: { dark: '#2B3A63', light: '#FFFFFF' } };

export default function PlancheQR() {
  const [qrs, setQrs] = useState<{ jeu: string; classement: string; ecran: string } | null>(null);

  const base = `${window.location.origin}${window.location.pathname}`;
  const urls = {
    jeu: `${base}#/`,
    classement: `${base}#/classement`,
    ecran: `${base}#/ecran`,
  };

  useEffect(() => {
    Promise.all([
      QRCode.toDataURL(urls.jeu, { ...OPTIONS, width: 640 }),
      QRCode.toDataURL(urls.classement, { ...OPTIONS, width: 400 }),
      QRCode.toDataURL(urls.ecran, { ...OPTIONS, width: 400 }),
    ]).then(([jeu, classement, ecran]) => setQrs({ jeu, classement, ecran }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!qrs) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <p className="font-script text-4xl text-encre">B &amp; W</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="planche-qr">
      <header className="no-impression sticky top-0 bg-white border-b border-brume z-10">
        <div className="max-w-2xl mx-auto px-5 h-14 flex items-center justify-between">
          <Link to="/greffier" className="min-h-[44px] flex items-center gap-1 text-encre-clair font-medium text-sm">
            <ChevronLeft size={18} /> Greffe
          </Link>
          <button data-testid="imprimer-btn" onClick={() => window.print()} className="min-h-[44px] px-5 rounded-full bg-encre text-white font-medium text-sm flex items-center gap-2">
            <Printer size={16} /> Imprimer
          </button>
        </div>
      </header>

      <p className="no-impression max-w-2xl mx-auto px-6 pt-4 text-sm text-encre-clair leading-relaxed">
        Deux feuilles A4 : la première pour l'affichage (les trois adresses), la seconde à découper
        pour les tables. Les QR codes pointent vers <strong>ce domaine</strong> — imprimez-les depuis
        le site définitif une fois déployé sur IONOS.
      </p>

      {/* Feuille 1 — affiche */}
      <section className="feuille-a4 max-w-2xl mx-auto px-8 py-10 text-center">
        <p className="petit-cap">Château de Saint-Senoch · 22 août 2026</p>
        <h1 className="font-script text-encre text-6xl mt-2">Le jeu de l'apéro</h1>
        <p className="text-encre font-semibold tracking-[0.3em] uppercase text-sm mt-1">Tatoo B/W</p>

        <div className="mt-8 inline-block border border-encre p-5">
          <img src={qrs.jeu} alt="QR du jeu" className="w-64 h-64 mx-auto" />
          <p className="font-semibold text-encre mt-3 text-lg">Scannez pour jouer</p>
          <p className="text-xs text-encre-clair mt-1 break-all">{urls.jeu}</p>
        </div>

        <div className="mt-8 grid grid-cols-2 gap-6 max-w-md mx-auto">
          <div className="border border-brume p-4">
            <img src={qrs.classement} alt="QR du classement" className="w-32 h-32 mx-auto" />
            <p className="font-medium text-encre mt-2 text-sm">Le classement en direct</p>
            <p className="text-[10px] text-encre-clair mt-1 break-all">{urls.classement}</p>
          </div>
          <div className="border border-brume p-4">
            <img src={qrs.ecran} alt="QR grand écran" className="w-32 h-32 mx-auto" />
            <p className="font-medium text-encre mt-2 text-sm">Vue grand écran (TV)</p>
            <p className="text-[10px] text-encre-clair mt-1 break-all">{urls.ecran}</p>
          </div>
        </div>

        <div className="flex justify-center mt-8">
          <BrancheFlorale className="w-56" />
        </div>
      </section>

      {/* Feuille 2 — cartes de table à découper */}
      <section className="feuille-a4 max-w-2xl mx-auto px-6 py-10">
        <p className="no-impression petit-cap text-center mb-4">Feuille 2 — cartes de table (à découper)</p>
        <div className="grid grid-cols-2 gap-0">
          {Array.from({ length: 6 }, (_, i) => (
            <div key={i} className="border border-dashed border-petale p-5 text-center">
              <p className="font-script text-encre text-3xl leading-none">Le jeu de l'apéro</p>
              <p className="text-encre font-semibold tracking-[0.25em] uppercase text-[10px] mt-1">Tatoo B/W</p>
              <img src={qrs.jeu} alt="QR du jeu" className="w-28 h-28 mx-auto mt-3" />
              <p className="text-xs font-medium text-encre mt-2">Scannez, jouez, gagnez le podium</p>
              <p className="text-[10px] text-encre-clair mt-1">3 actes · 1 chrono · 10 tatoués</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
